import { ReactNode, useEffect } from "react";
import { useLocation } from "react-router-dom";
import type { Theme } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import { ClientPortalGroupAccessProvider } from "context/GroupAccessContext";
import { setMiniSidenav, useMaterialUIController } from "context";
import Sidenav from "components/Sidenav";
import MDBox from "components/MDBox";
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";
import type { ClientRoute } from "routes";

/** Material Dashboard theme extends MUI with helper functions. */
type MdTheme = Theme & {
  functions: {
    pxToRem: (value: number) => string;
  };
};

type ClientMainLayoutProps = {
  children: ReactNode;
  routes: ClientRoute[];
};

export default function ClientMainLayout({ children, routes }: ClientMainLayoutProps) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, darkMode } = controller as {
    miniSidenav: boolean;
    darkMode: boolean;
  };
  const { pathname } = useLocation();
  const isAuthRoute = pathname.includes("/auth");

  useEffect(() => {
    const openSidenavOnDesktop = () => {
      if (window.innerWidth >= 1200) {
        setMiniSidenav(dispatch, false);
      }
    };

    openSidenavOnDesktop();
    window.addEventListener("resize", openSidenavOnDesktop);
    return () => window.removeEventListener("resize", openSidenavOnDesktop);
  }, [dispatch]);

  return (
    <ClientPortalGroupAccessProvider>
      {!isAuthRoute && (
        <>
          <Sidenav
            color="info"
            brand={darkMode ? brandDark : brandWhite}
            brandName="NanoTech ClientZone"
            routes={routes}
          />
          {miniSidenav && (
            <MDBox
              sx={{
                position: "fixed",
                top: 12,
                left: 12,
                zIndex: 1300,
                display: { xs: "block", xl: "none" },
              }}
            >
              <IconButton
                color="info"
                aria-label="Open navigation"
                onClick={() => setMiniSidenav(dispatch, false)}
                sx={{
                  bgcolor: "background.paper",
                  boxShadow: 2,
                  "&:hover": { bgcolor: "background.paper" },
                }}
              >
                <MenuIcon />
              </IconButton>
            </MDBox>
          )}
        </>
      )}

      {/* Outer shell mirrors AdminZone MainLayout (overflow + negative margins). */}
      <MDBox
        sx={(themeArg: Theme) => {
          const theme = themeArg as MdTheme;
          return {
            p: { xs: 0.5, md: 2 },
            position: "relative",
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minWidth: 0,
            width: "100%",
            maxWidth: "100%",
            overflowX: "hidden",
            [theme.breakpoints.down("xl")]: {
              marginLeft: -8,
              marginRight: -50,
              padding: "0.5rem",
              paddingTop: "0rem",
              transition: theme.transitions.create(["margin-left", "margin-right", "width"], {
                easing: theme.transitions.easing.easeInOut,
                duration: theme.transitions.duration.standard,
              }),
              "& > *": {
                maxWidth: "100%",
                width: "100%",
              },
            },
            [theme.breakpoints.up("xl")]: {
              marginLeft: miniSidenav ? theme.functions.pxToRem(-130) : theme.functions.pxToRem(-280),
              transition: theme.transitions.create(["margin-left", "margin-right", "width"], {
                easing: theme.transitions.easing.easeInOut,
                duration: theme.transitions.duration.standard,
              }),
            },
          };
        }}
      >
        {/* Inner content mirrors AdminZone DashboardLayout sidenav inset. */}
        <MDBox
          flex={1}
          sx={(themeArg: Theme) => {
            const theme = themeArg as MdTheme;
            return {
              position: "relative",
              maxWidth: "100%",
              minWidth: 0,
              [theme.breakpoints.down("xl")]: {
                width: "100%",
                px: 1,
                py: 2,
                "& > *": {
                  maxWidth: "100%",
                  overflowX: "auto",
                },
                "& .MuiCard-root": {
                  mx: 0,
                  width: "100%",
                },
              },
              [theme.breakpoints.up("xl")]: {
                // Soft UI DashboardLayout: positive inset without width:100%
                marginLeft: miniSidenav ? theme.functions.pxToRem(120) : theme.functions.pxToRem(274),
                pl: { md: 1 },
                pr: { md: 1 },
                transition: theme.transitions.create(["margin-left", "margin-right"], {
                  easing: theme.transitions.easing.easeInOut,
                  duration: theme.transitions.duration.standard,
                }),
              },
            };
          }}
        >
          {children}
        </MDBox>
      </MDBox>
    </ClientPortalGroupAccessProvider>
  );
}
